import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
    AppBar,
    Box,
    Toolbar,
    IconButton,
    Typography,
    Menu,
    Container,
    Avatar,
    Button,
    Tooltip,
    MenuItem,
    Chip,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SwitchAccountIcon from "@mui/icons-material/SwapHoriz";
import { fetchUser, getCompanyInfo, getCompanyInfoWithUserId, switchToAdminAccount, checkHasAdminAccount, fetchMyStatus } from "../../api/api";
import { LEVEL_COLORS } from "../LevelAvatar/LevelAvatar";
import { getUserId, removeCookie, getCookie, bootstrapSwitchAuthFromUrl, clearAuthCookies } from "../../utils/cookieUtils";
import Swal from "sweetalert2";

const tabs = [
    { name: "HOME", route: "/dashboard/home" },
    { name: "TRAINING", route: "/dashboard/training" },
    /*{ name: "UPDATES", route: "/dashboard/updates" },*/
    { name: "PROFILE", route: "/dashboard/profile" },
];

const Dashboard = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [anchorElNav, setAnchorElNav] = React.useState(null);
    const [anchorElUser, setAnchorElUser] = React.useState(null);
    const [userId, setUserId] = useState(null);
    const [status, setStatus] = useState(null);
    const [isTrial, setIsTrial] = useState(false);
    const [daysRemaining, setDaysRemaining] = useState(null);
    const [companyName, setCompanyName] = useState(null);
    const [isSwitching, setIsSwitching] = useState(false);
    const [hasAdminAccount, setHasAdminAccount] = useState(false);
    const [userData, setUserData] = useState({
        profile_image: null,
        first_name: "",
        last_name: "",
    });
    // Bronze / Silver / Gold ring colour for the header avatar. Null until the
    // employee has read enough posts to have a status, or if points are off.
    const [levelColor, setLevelColor] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        let cancelled = false;

        fetchMyStatus()
            .then(({ data }) => {
                if (cancelled) return;
                setLevelColor(LEVEL_COLORS[data?.level] || null);
            })
            .catch(() => {
                // Points may be turned off for this company - leave the avatar plain.
                if (!cancelled) setLevelColor(null);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        // App.js usually bootstraps switch cookies first; keep a fallback here
        const search = location.search || window.location.search;
        const switchAuth = bootstrapSwitchAuthFromUrl(search);
        const activeUserId = switchAuth.userId || getUserId();
        const activeCompanyId = switchAuth.companyId || getCookie('company_id');

        if (!activeUserId) {
            navigate("/");
            return;
        }

        setUserId(activeUserId);
        // Notify App after token cookies are written so checkAuth does not race
        window.dispatchEvent(new Event('authUpdated'));

        // Defer API calls one tick so Authorization interceptor reads the new token
        const loadId = window.setTimeout(() => {
            fetchUser(activeUserId)
                .then((response) => {
                    setStatus(response.data.status);
                    setUserData({
                        profile_image: response.data.profile_image || null,
                        first_name: response.data.first_name || "",
                        last_name: response.data.last_name || "",
                    });
                })
                .catch((error) => {
                    console.error("Error fetching user:", error);
                });

            const fetchCompanyInfo = async () => {
                try {
                    const companyInfo = switchAuth.didBootstrap
                        ? await getCompanyInfoWithUserId(activeUserId, activeCompanyId)
                        : await getCompanyInfo();
                    if (companyInfo.status && companyInfo.company) {
                        setIsTrial(companyInfo.company.is_trial);
                        setDaysRemaining(companyInfo.company.days_remaining);
                        setCompanyName(companyInfo.company.name);
                    }
                } catch (error) {
                    console.error('Error fetching company info:', error);
                }
            };

            fetchCompanyInfo();

            checkHasAdminAccount().then(result => {
                setHasAdminAccount(result.has_admin_account || false);
            }).catch(err => {
                console.error('Error checking admin account:', err);
                setHasAdminAccount(false);
            });
        }, 0);

        return () => window.clearTimeout(loadId);
    }, [navigate, location]);

    const handleLogout = () => {
        // Only clear user session — never wipe admin cookies on localhost
        clearAuthCookies();
        navigate("/"); // Redirect to login
    };

    const handleSwitchToAdmin = async () => {
        setIsSwitching(true);
        
        try {
            // Store current user_id temporarily in localStorage
            const userId = getCookie('user_id');
            localStorage.setItem('switched_from_user_id', userId);
            
            // Call switch API
            const response = await switchToAdminAccount();
            
            if (response.status && response.admin) {
                // Remove user_id cookie
                removeCookie('user_id');
                
                // ✅ Build URL with token if provided
                const tokenParam = response.token ? `&token=${encodeURIComponent(response.token)}` : '';
                
                // Navigate to admin dashboard with admin_id and token as query parameters
                // This allows cookies to be set on the admin domain
                const adminUrl = window.location.hostname === 'localhost' 
                    ? `http://localhost:3000/admindashboard/AdminHome?admin_id=${response.admin.id}&company_id=${response.admin.company_id || ''}${tokenParam}` 
                    : window.location.hostname === 'dev-user.easyknow.eu'
                    ? `https://dev-admin.easyknow.eu/admindashboard/AdminHome?admin_id=${response.admin.id}&company_id=${response.admin.company_id || ''}${tokenParam}`
                    : `https://admin.easyknow.eu/admindashboard/AdminHome?admin_id=${response.admin.id}&company_id=${response.admin.company_id || ''}${tokenParam}`;
                
                window.location.href = adminUrl;
                setIsSwitching(false);
            } else {
                //alert(response.message || 'Failed to switch to admin account');
                Swal.fire({
                    icon: 'error',
                    title: 'Failed to switch to admin account',
                    text: response.message || 'Failed to switch to admin account',
                });
                setIsSwitching(false);
            }
        } catch (error) {
            console.error('Error switching to admin account:', error);
            const errorMessage = error.response?.data?.message || 'Failed to switch to admin account. Please try again.';
            //alert(errorMessage);
            Swal.fire({
                icon: 'error',
                title: 'Failed to switch to admin account',
                text: errorMessage,
            });
            setIsSwitching(false);
        }
    };


    return (
        <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
            {/* AppBar */}
            <AppBar
                position="static"
                sx={{
                    background: "linear-gradient(to right, #3b82f6, #2563eb)", // Blue gradient
                }}
            >
                <Container maxWidth="xl">
                    <Toolbar 
                        disableGutters
                        sx={{
                            minHeight: { xs: '56px', sm: '64px' },
                            px: { xs: 0.5, sm: 2 },
                            flexWrap: 'nowrap',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Mobile Menu */}
                        <Box
                            sx={{
                                flexGrow: 1,
                                display: { xs: "flex", md: "none" },
                            }}
                        >
                            <IconButton
                                size="large"
                                onClick={(e) => setAnchorElNav(e.currentTarget)}
                                color="inherit"
                            >
                                <MenuIcon />
                            </IconButton>
                            <Menu
                                anchorEl={anchorElNav}
                                open={Boolean(anchorElNav)}
                                onClose={() => setAnchorElNav(null)}
                            >
                                {tabs.map((tab) => (
                                    <MenuItem
                                        key={tab.name}
                                        onClick={() => navigate(tab.route)}
                                    >
                                        <Typography textAlign="center">
                                            {tab.name}
                                        </Typography>
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>

                        {/* Desktop Menu */}
                        <Box
                            sx={{
                                flexGrow: 1,
                                display: { xs: "none", md: "flex" },
                            }}
                        >
                            {tabs.map((tab) => (
                                <Button
                                    key={tab.name}
                                    onClick={() => navigate(tab.route)}
                                    sx={{ my: 2, color: "white" }}
                                >
                                    {tab.name}
                                </Button>
                            ))}
                        </Box>

                        {/* User Settings with Company Name and Beta Badge */}
                        <Box sx={{ 
                            flexGrow: 0, 
                            gap: { xs: 0.5, sm: 1 }, 
                            display: "flex", 
                            alignItems: "center",
                            flexWrap: { xs: 'nowrap', sm: 'wrap' },
                            overflow: 'hidden',
                            maxWidth: { xs: '100%', sm: 'none' },
                        }}>

                            {/* updates chip */}
                           
                            {/* Company Name - Visible on mobile with truncation */}

                            {companyName && (
                                <Chip
                                    label={companyName}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                        height: { xs: '24px', sm: '28px' },
                                        borderColor: 'rgba(255, 255, 255, 0.5)',
                                        color: 'white',
                                        maxWidth: { xs: '80px', sm: 'none' },
                                        '& .MuiChip-label': {
                                            px: { xs: 0.75, sm: 1.5 },
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        },
                                    }}
                                />
                            )}

                            {/* updates chip */}
                            <Chip
                                label="Updates"
                                onClick={() => navigate("/dashboard/updates")}
                                color="primary"
                                variant="outlined"
                                size="small"
                                sx={{ fontWeight: 600, fontSize: { xs: '0.65rem', sm: '0.75rem' }, height: { xs: '24px', sm: '28px' }, borderColor: 'rgba(255, 255, 255, 0.5)', color: 'white', maxWidth: { xs: '80px', sm: 'none' }, '& .MuiChip-label': { px: { xs: 0.75, sm: 1.5 }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                            />
                            
                            {/* Beta Badge - only show for trial accounts - Responsive text */}
                            {isTrial && (
                                <Chip
                                    label={
                                        isMobile 
                                            ? `Trial${daysRemaining !== null && daysRemaining >= 0 ? ` ${daysRemaining}d` : ''}`
                                            : `Free Premium Trial${daysRemaining !== null && daysRemaining >= 0 ? ` - ${daysRemaining} days left` : ''}`
                                    }
                                    color="warning"
                                    size="small"
                                    sx={{
                                        fontWeight: 'bold',
                                        fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                        height: { xs: '22px', sm: '24px' },
                                        maxWidth: { xs: '100px', sm: 'none' },
                                        '& .MuiChip-label': {
                                            px: { xs: 0.75, sm: 1 },
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        },
                                    }}
                                />
                            )}
                            
                            {/* Switch to Admin Chip - Only show if user has admin account - Visible on mobile */}
                            {hasAdminAccount && (
                                <Tooltip title={isSwitching ? 'Switching...' : 'Switch to Admin Account'}>
                                    <Chip
                                        icon={<SwitchAccountIcon sx={{ fontSize: { xs: '14px', sm: '16px' } }} />}
                                        label={isSwitching ? 'Switching...' : isMobile ? 'Admin' : 'To Admin'}
                                        onClick={handleSwitchToAdmin}
                                        disabled={isSwitching}
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            color: 'white',
                                            borderColor: 'rgba(255, 255, 255, 0.5)',
                                            fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                            fontWeight: 500,
                                            height: { xs: '24px', sm: '28px' },
                                            cursor: isSwitching ? 'not-allowed' : 'pointer',
                                            '&:hover': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                borderColor: 'rgba(255, 255, 255, 0.8)',
                                            },
                                            '&.Mui-disabled': {
                                                color: 'rgba(255, 255, 255, 0.5)',
                                                borderColor: 'rgba(255, 255, 255, 0.3)',
                                            },
                                            '& .MuiChip-icon': {
                                                color: 'white',
                                            },
                                            '& .MuiChip-label': {
                                                px: { xs: 0.5, sm: 1 },
                                            },
                                        }}
                                    />
                                </Tooltip>
                            )}
                            
                            <Tooltip title="Open settings">
                                <IconButton
                                    onClick={(e) =>
                                        setAnchorElUser(e.currentTarget)
                                    }
                                    sx={{ 
                                        p: { xs: 0.5, sm: 0 },
                                        ml: { xs: 0.5, sm: 0 },
                                    }}
                                >
                                    <Avatar
                                        alt="User Avatar"
                                        src={userData.profile_image || undefined}
                                        sx={{
                                            bgcolor: "rgba(255, 255, 255, 0.2)",
                                            width: { xs: 32, sm: 40 },
                                            height: { xs: 32, sm: 40 },
                                            fontSize: { xs: '0.875rem', sm: '1rem' },
                                            // Bronze / Silver / Gold ring. Drawn with box-shadow so
                                            // it adds no width and the header layout is unchanged.
                                            ...(levelColor
                                                ? {
                                                      boxShadow: `0 0 0 2px rgba(255,255,255,0.9), 0 0 0 4px ${levelColor}`,
                                                  }
                                                : {}),
                                        }}
                                    >
                                        {!userData.profile_image && 
                                            `${userData.first_name?.[0] || ""}${userData.last_name?.[0] || ""}`}
                                    </Avatar>
                                </IconButton>
                            </Tooltip>
                            {/* <Tooltip title="User Status"> */}
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    ml: { xs: 0.5, sm: 1 },
                                }}
                            >
                                <Chip
                                    label={status}
                                    color={
                                        status?.toUpperCase() === "ACTIVE"
                                            ? "success"
                                            : status?.toUpperCase() === "PENDING"
                                            ? "warning"
                                            : status?.toUpperCase() === "HOLD"
                                            ? "info"
                                            : "error"
                                    }
                                    variant="filled" // or "outlined" if you prefer
                                    size="small"
                                    sx={{
                                        fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                        height: { xs: '22px', sm: '24px' },
                                        '& .MuiChip-label': {
                                            px: { xs: 0.75, sm: 1 },
                                        },
                                    }}
                                />

                                <Menu
                                    anchorEl={anchorElUser}
                                    open={Boolean(anchorElUser)}
                                    onClose={() => setAnchorElUser(null)}
                                >
                                    <MenuItem
                                        onClick={() => {
                                            setAnchorElUser(null);
                                            navigate("/dashboard/profile");
                                        }}
                                    >
                                        <Typography textAlign="center">
                                            Profile
                                        </Typography>
                                    </MenuItem>
                                    <MenuItem onClick={handleLogout}>
                                        <Typography textAlign="center">
                                            Logout
                                        </Typography>
                                    </MenuItem>
                                </Menu>
                            </Box>
                            {/* </Tooltip> */}
                        </Box>
                    </Toolbar>
                </Container>
            </AppBar>

            {/* Main Content */}
            <Box sx={{ flexGrow: 1, overflow: "auto" }}>
                <Outlet />
            </Box>
        </Box>
    );
};

export default Dashboard;
